import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { MapPin, Phone, Globe, ArrowLeft, ChevronRight } from 'lucide-react';
import SEO from './SEO';
import CategoryNav from './CategoryNav';
import { useManagedCollection, slugify } from '../admin/useManagedCollection';
import { VENUES, type Venue } from './Venues';
import SocialLinks from './SocialLinks';

const BASE_URL = 'https://dallasdesiscoop.com';

const FALLBACK_IMAGE =
  'https://images.unsplash.com/photo-1540575467063-178a50c2df87?w=1200&h=800&fit=crop';

// Same slug derivation the category page uses (slugify of name), exported here
// so card links and the detail page always agree — including admin-added venues.
export function venueSlug(name: string) {
  return (name || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');
}

function mapsUrl(v: Venue) {
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${v.name}, ${v.address}`)}`;
}

export default function VenueDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const mc = useManagedCollection<any>('venues', VENUES, (v, i) =>
    slugify(v.name || String(i))
  );

  const entry: Venue | undefined = mc.items.find(
    (v: any) => venueSlug(v.name) === slug
  );

  if (mc.loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="w-8 h-8 border-2 border-[#082f6d] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!entry) {
    return (
      <div
        className="min-h-screen bg-white flex flex-col items-center justify-center px-4"
        style={{ fontFamily: "'Poppins', sans-serif" }}
      >
        <SEO
          title="Party Venue Not Found | Dallas Desi Scoop"
          description="This party venue listing could not be found."
          canonical="/party-venues"
          noindex
        />
        <p className="text-gray-500 text-lg mb-4">Party venue not found.</p>
        <Link
          to="/party-venues"
          className="text-[#082f6d] font-semibold hover:underline"
        >
          ← Back to Party Venues
        </Link>
      </div>
    );
  }

  const typeLabel = entry.type || 'Banquet Hall';
  const city = entry.city || entry.address?.split(',').slice(-2, -1)[0]?.trim() || 'DFW';
  const title = `${entry.name} in ${city}, TX | Party Venue & Event Space | Dallas Desi Scoop`;
  const description = `${entry.name} is a party venue and event space in ${city}, TX. Find address, contact details, event types, and booking info for weddings, birthdays, and celebrations across DFW.`;
  const canonicalPath = `/party-venues/${slug}`;

  // Split the stored address into street + locality for structured data.
  const addressParts = (entry.address || '').split(',').map((p) => p.trim()).filter(Boolean);
  const streetAddress =
    addressParts.length >= 3 ? addressParts.slice(0, -2).join(', ') : undefined;
  const addressLocality =
    addressParts.length >= 2 ? addressParts[addressParts.length - 2] : city;

  const jsonLd = [
    {
      '@type': 'EventVenue',
      name: entry.name,
      image: entry.image || FALLBACK_IMAGE,
      description: entry.description || 'Party venue and event space in Dallas-Fort Worth',
      address: {
        '@type': 'PostalAddress',
        streetAddress: streetAddress,
        addressLocality: addressLocality || undefined,
        addressRegion: 'TX',
        addressCountry: 'US',
      },
      telephone: entry.contactNumber || undefined,
      url: entry.website || `${BASE_URL}${canonicalPath}`,
    },
    {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: `${BASE_URL}/` },
        { '@type': 'ListItem', position: 2, name: 'Party Venues', item: `${BASE_URL}/party-venues` },
        { '@type': 'ListItem', position: 3, name: entry.name, item: `${BASE_URL}${canonicalPath}` },
      ],
    },
  ];

  return (
    <div className="min-h-screen bg-[#f9fafb]" style={{ fontFamily: "'Poppins', sans-serif" }}>
      <SEO
        title={title}
        description={description}
        canonical={canonicalPath}
        ogImage={entry.image || FALLBACK_IMAGE}
        jsonLd={jsonLd}
      />

      {/* Hero */}
      <section className="relative w-full overflow-hidden" style={{ minHeight: '260px' }}>
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url("${entry.image || FALLBACK_IMAGE}")` }}
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              'linear-gradient(to bottom, rgba(8,47,109,0.75) 0%, rgba(8,47,109,0.55) 55%, rgba(8,47,109,0.88) 100%)',
          }}
        />

        <div className="relative z-10 max-w-[1000px] mx-auto px-4 pt-6 pb-10 flex flex-col">
          <motion.div
            initial={{ opacity: 0, x: -8 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.4 }}
            className="self-start flex items-center gap-1.5 mb-5"
          >
            <Link
              to="/party-venues"
              className="flex items-center gap-1.5 text-white/80 hover:text-white text-[12px] font-medium transition-colors"
            >
              <ArrowLeft className="w-3.5 h-3.5" />
              Party Venues
            </Link>
            <ChevronRight className="w-3 h-3 opacity-60 text-white/60" />
            <span className="text-white/60 text-[12px] font-medium">{entry.name}</span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="text-white text-[26px] sm:text-[36px] font-extrabold leading-tight tracking-tight"
            style={{ textShadow: '0 2px 8px rgba(0,0,0,0.4)' }}
          >
            {entry.name}
          </motion.h1>

          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.18 }}
            className="flex flex-wrap items-center gap-3 mt-3"
          >
            {entry.address && (
              <span className="flex items-center gap-1 text-white/85 text-[13px]">
                <MapPin className="w-3.5 h-3.5 text-[#51ba4f]" />
                {entry.address}
              </span>
            )}
            <span className="text-white/40">·</span>
            <span className="text-white/75 text-[13px]">{typeLabel}</span>
          </motion.div>
        </div>
      </section>

      <CategoryNav />

      {/* Main content */}
      <div className="max-w-[1000px] mx-auto px-4 py-10 grid grid-cols-1 md:grid-cols-3 gap-8">
        <div className="md:col-span-2">
          <h2 className="text-[18px] font-bold text-gray-900 mb-3">About {entry.name}</h2>
          <p className="text-[14px] text-gray-600 leading-relaxed max-w-[60ch]">
            {entry.description
              ? entry.description
              : `${entry.name} is a party venue and event space serving the South Asian community across Dallas–Fort Worth`}
            {entry.city ? `, located in ${entry.city}, TX` : ''}.
            {' '}It hosts weddings, receptions, birthdays, engagements, anniversaries, baby showers, cultural celebrations, corporate events, and private parties for families across DFW.
            {entry.address ? ` Located at ${entry.address}.` : ''}
          </p>
          <h2 className="text-[18px] font-bold text-gray-900 mt-8 mb-3">Events & Celebrations</h2>
          <p className="text-[14px] text-gray-600 leading-relaxed max-w-[60ch]">
            Looking for an Indian party venue, Pakistani event venue, South Asian banquet hall, restaurant party venue, wedding venue, or private event space in {city}? {entry.name} offers space for celebrations of every size, from intimate gatherings to large wedding receptions.
          </p>
          <div className="mt-6 max-w-xs">
            <SocialLinks item={{ ...entry, website: undefined }} variant="bar" />
          </div>
        </div>

        <div
          className="bg-white rounded-xl border border-gray-100 p-5 h-fit"
          style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}
        >
          <h3 className="text-[13px] font-bold uppercase tracking-wide text-gray-400 mb-4">
            Contact & Info
          </h3>
          <div className="space-y-3 text-[13px] text-gray-700">
            <div className="flex items-start gap-2">
              <MapPin className="w-4 h-4 text-[#51ba4f] shrink-0 mt-0.5" />
              <span>{entry.address || '[Coming soon]'}</span>
            </div>
            {entry.contactNumber && (
              <div className="flex items-start gap-2">
                <Phone className="w-4 h-4 text-[#51ba4f] shrink-0 mt-0.5" />
                <a href={`tel:${entry.contactNumber.replace(/[^\d+]/g, '')}`} className="hover:underline">
                  {entry.contactNumber}
                </a>
              </div>
            )}
            {entry.website && (
              <div className="flex items-start gap-2">
                <Globe className="w-4 h-4 text-[#51ba4f] shrink-0 mt-0.5" />
                <a
                  href={entry.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:underline break-all"
                >
                  Visit Website
                </a>
              </div>
            )}
          </div>
          {entry.address && (
            <a
              href={mapsUrl(entry)}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-5 block text-center bg-[#082f6d] hover:bg-[#062454] text-white text-[13px] font-semibold py-2.5 rounded-lg transition-colors"
            >
              Get Directions
            </a>
          )}
          <Link
            to="/party-venues"
            className="mt-3 block text-center text-[#082f6d] text-[12px] font-semibold hover:underline"
          >
            Browse all party venues
          </Link>
        </div>
      </div>
    </div>
  );
}